import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { store } from '../../store';
import { setExperienceLive, ExhibitId } from '../../store/slices/exhibitSlice';
import { useOrangLautZone } from '../../hooks/useZoneStatus';
import TcPaper from '../TcPaper';
import TcCard from '../TcCard';
import TcScanQrCodeBar from '../TcScanQrCodeBar';
import TcCrossFadeImage from '../TcCrossFadeImage';
import TcPopupR65 from '../TcPopupR65';
import { useSimpleImagePreloader } from '../../hooks/useImagePreloader';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { ZONE_STATUS_POLLING_INTERVAL_MS, getZonePollingIntervalSeconds } from '../../utils/constants';

import LoadingScreen from '../shared/LoadingScreen';

import imageOrangLaut00 from '../../assets/images/exhibit/orang-laut/img_orang-laut_00.jpg';
import imageOrangLaut01 from '../../assets/images/exhibit/orang-laut/img_orang-laut_01.jpg';
import imageOrangLaut02 from '../../assets/images/exhibit/orang-laut/img_orang-laut_02.jpg';
import imageOrangLaut03 from '../../assets/images/exhibit/orang-laut/img_orang-laut_03.jpg';
import imageOrangLaut04 from '../../assets/images/exhibit/orang-laut/img_orang-laut_04.jpg';
import imageOrangLautBtn01 from '../../assets/images/exhibit/orang-laut/img_btn_orang-laut_01.png';
import imageOrangLautBtn02 from '../../assets/images/exhibit/orang-laut/img_btn_orang-laut_02.png';
import imageOrangLautBtn03 from '../../assets/images/exhibit/orang-laut/img_btn_orang-laut_03.png';
import imageOrangLautBtn04 from '../../assets/images/exhibit/orang-laut/img_btn_orang-laut_04.png';

// Interface for claim result (Redux-only version)
interface ClaimResult {
    type: string;
    payload?: any;
    error?: any;
}

// Helper function to get visitor ID with fallback (Redux-only version)
const getVisitorIdWithFallback = (): string | null => {
    // Priority: Redux -> localStorage -> generate anonymous ID
    try {
        // Try to get from Redux first (assuming store is accessible)
        const state = store.getState();
        const reduxVisitorId = state.user.visitorId;
        if (reduxVisitorId) return reduxVisitorId;
    } catch (error) {
        console.warn('Could not access Redux store for visitor ID:', error);
    }
    
    // Fallback to localStorage
    const localStorageVisitorId = localStorage.getItem('visitorId');
    if (localStorageVisitorId) return localStorageVisitorId;
    
    // Generate anonymous ID for session consistency
    const anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitorId', anonymousId);
    return anonymousId;
};

export default function OrangLaut() {
    const { t } = useTranslation();
    const { exhibitId } = useParams<{ exhibitId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    
    // Get current user's visitorId from Redux (with fallback)
    const { visitorId: reduxVisitorId, name, email, isAuthenticated, hasCompletedRegistration } = useAppSelector(state => state.user);
    
    // Use enhanced fallback strategy for visitor ID (Frontend-only mode)
    const visitorId = getVisitorIdWithFallback() || undefined;
    
    // Debug user state
    useEffect(() => {
        console.log('🔍 Current user state (Frontend-only mode):', {
            reduxVisitorId,
            effectiveVisitorId: visitorId,
            fallbackUsed: visitorId !== reduxVisitorId,
            hasVisitorId: !!visitorId,
            visitorIdType: typeof visitorId,
            visitorIdLength: visitorId?.length,
            name,
            email,
            isAuthenticated,
            hasCompletedRegistration
        });
    }, [reduxVisitorId, visitorId, name, email, isAuthenticated, hasCompletedRegistration]);
    
    const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
    const [displayedImage, setDisplayedImage] = useState(imageOrangLaut00);
    const [previousImage, setPreviousImage] = useState(imageOrangLaut00);
    const [showDisplayedImage, setShowDisplayedImage] = useState(true);

    const [showZoneBusyPopup, setShowZoneBusyPopup] = useState(false);
    const [busyPopupCountdown, setBusyPopupCountdown] = useState(20);
    const [isCountdownActive, setIsCountdownActive] = useState(false);
    const [showZoneTimeoutPopup, setShowZoneTimeoutPopup] = useState(false);
    const [userHasActiveSession, setUserHasActiveSession] = useState(false);

    // State for QR scan processing
    const [qrScanProcessed, setQrScanProcessed] = useState(false);
    const qrScanClaimInitiated = useRef(false);

    // Button cooldown state to prevent button bashing
    const [buttonCooldown, setButtonCooldown] = useState(false);
    const [cooldownTimer, setCooldownTimer] = useState<NodeJS.Timeout | null>(null);
    const BUTTON_COOLDOWN_DURATION = 500; // 0.5 second to prevent button bashing

    // Zone timeout timer (separate from global exhibit timeout)
    const zoneTimeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
    const ZONE_TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes for zone timeout

    // Zone status polling system (configurable interval, default 4 seconds)
    // Use refs instead of state to avoid React Strict Mode issues
    const statusPollingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isPollingActive, setIsPollingActive] = useState(false);

    // Track if QR scan has been processed to avoid duplicate processing
    const qrScanProcessedRef = useRef<string | null>(null);

    // Note: Global timeout and busy systems not used for zone-based exhibits
    
    // Zone status management for Orang Laut zone (4.1) - now Redux-only
    const {
        zoneStatus,
        isLoading: zoneLoading,
        error: zoneError,
        isAvailable,
        isProcessing,
        fetchZoneStatus,
        claimZone,
        updatePayload,
        updateStatus,
        clearError: clearZoneError,
    } = useOrangLautZone(visitorId);

    // Device detection
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    // Zone ownership check - works with Redux-only visitor IDs
    const isClaimedByCurrentUser = visitorId && zoneStatus?.claimedBy === visitorId;
    
    // Zone availability check - but respect user session even during conflicts
    const isAvailableToClaim = isAvailable && !isClaimedByCurrentUser && !userHasActiveSession;
    
    // Enhanced interaction permission that considers user session during conflicts
    const canUserInteract = () => {
        // For registered users: check if zone is claimed by them OR they have an active session
        if (visitorId) {
            return isClaimedByCurrentUser || userHasActiveSession;
        }
        // For non-registered users: check if they have an active session
        return userHasActiveSession;
    };
    
    // Debug logging for user ID comparison
    useEffect(() => {
        if (zoneStatus?.claimedBy && visitorId) {
            console.log('🔍 User ID comparison:', {
                visitorId,
                claimedBy: zoneStatus.claimedBy,
                isMatch: zoneStatus.claimedBy === visitorId,
                isClaimedByCurrentUser,
                zoneStatus: zoneStatus.status,
                hasClientSideMapping: !!(zoneStatus as any)._clientSideMapped,
                timestamp: new Date().toISOString()
            });
        }
    }, [zoneStatus?.claimedBy, visitorId, isClaimedByCurrentUser, zoneStatus?.status]);

    // Preload all images when component mounts
    const allImages = [
        imageOrangLaut00,
        imageOrangLaut01,
        imageOrangLaut02,
        imageOrangLaut03,
        imageOrangLaut04,
        imageOrangLautBtn01,
        imageOrangLautBtn02,
        imageOrangLautBtn03,
        imageOrangLautBtn04
    ];
    
    const { isLoading, isComplete } = useSimpleImagePreloader(allImages);
    
    
    // Image button data
    const imageButtons = [
        {
            id: 'btn01',
            image: imageOrangLautBtn01,
            title: t('exhibits.orangLaut.buttons.btn01.title'),
            content: t('exhibits.orangLaut.buttons.btn01.content')
        },
        {
            id: 'btn02',
            image: imageOrangLautBtn02,
            title: t('exhibits.orangLaut.buttons.btn02.title'),
            content: t('exhibits.orangLaut.buttons.btn02.content')
        },
        {
            id: 'btn03',
            image: imageOrangLautBtn03,
            title: t('exhibits.orangLaut.buttons.btn03.title'),
            content: t('exhibits.orangLaut.buttons.btn03.content')
        },
        {
            id: 'btn04',
            image: imageOrangLautBtn04,
            title: t('exhibits.orangLaut.buttons.btn04.title'),
            content: t('exhibits.orangLaut.buttons.btn04.content')
        }
    ];
    

    // Handle image button clicks with zone payload updates (following diagram flow)
    const handleButtonClick = async (buttonId: string, buttonTitle: string, optionValue: number) => {
        // Check if buttons are in cooldown period
        if (buttonCooldown) {
            console.log('⏸️ Button cooldown active - ignoring click');
            return;
        }

        // Check: Zone must be claimed by current user (or active session for anonymous users)
        // For registered users: check if zone is claimed by them OR they have an active session
        // For non-registered users: check if they have an active session (meaning they've scanned QR and claimed the zone)
        const canInteract = canUserInteract();
        
        if (!canInteract) {
            console.log('❌ Cannot interact with buttons - zone claimed by another user');
            setShowZoneBusyPopup(true);
            setBusyPopupCountdown(20);
            setIsCountdownActive(false);
            return;
        }

        console.log(`🎯 Button clicked: ${buttonId} with option: ${optionValue}`);

        // Start button cooldown immediately to prevent rapid clicks
        startButtonCooldown();

        // Check if option button pressed? (from diagram)
        if (optionValue > 0) {
            try {
                // PUT zone-status/{zone_id}/payload with option = option_id (int)
                console.log('🔍 Before updatePayload - Current zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    userHasActiveSession,
                    canInteract,
                    visitorId
                });

                // Additional pre-flight validation
                const preFlightCheck = {
                    hasVisitorId: !!visitorId,
                    zoneClaimedByUser: zoneStatus?.claimedBy === visitorId,
                    userHasSession: userHasActiveSession,
                    canProceed: (zoneStatus?.claimedBy === visitorId) || userHasActiveSession
                };
                
                console.log('🛂 Pre-flight validation for payload update:', preFlightCheck);
                
                if (!preFlightCheck.canProceed) {
                    console.warn('❌ Pre-flight check failed - aborting payload update');
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(20);
                    setIsCountdownActive(false);
                    return;
                }

                const payloadResult = await updatePayload({
                    payload: {
                        option: optionValue,
                    },
                });

                console.log('🔍 After updatePayload - API response:', payloadResult);
                console.log('🔍 After updatePayload - Updated zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    userHasActiveSession,
                    canInteract: canUserInteract(),
                    visitorId
                });

                console.log(`✅ Zone payload updated with option: ${optionValue}`);

                // Update selected state for highlighting (highlight option)
                setSelectedImageId(buttonId);
                setPreviousImage(displayedImage);
                setDisplayedImage(getImageForId(buttonId));
                setShowDisplayedImage(!showDisplayedImage);

                // Dispatch experience live status
                dispatch(setExperienceLive(true));

                // Reset zone timeout since user is actively interacting
                resetZoneTimeout();
                startZoneTimeout();

            } catch (error: any) {
                console.error(`❌ Failed to update zone payload with option ${optionValue}:`, error);
                
                // Handle specific error types
                if (error?.status === 409) {
                    console.warn('⚠️ Zone payload update conflict (409) - maintaining user session');
                    
                    // For 409 conflicts, maintain user session and UI state regardless of API response
                    console.log('🔄 Maintaining user session and UI state during 409 conflict');
                    
                    // Ensure user session remains active
                    if (!userHasActiveSession) {
                        setUserHasActiveSession(true);
                        console.log('✅ User session activated to maintain interaction during conflict');
                    }
                    
                    // Still update UI optimistically since user interaction was valid
                    setSelectedImageId(buttonId);
                    setPreviousImage(displayedImage);
                    setDisplayedImage(getImageForId(buttonId));
                    setShowDisplayedImage(!showDisplayedImage);
                    dispatch(setExperienceLive(true));
                    resetZoneTimeout();
                    startZoneTimeout();
                    
                    console.log('✅ UI state maintained during 409 conflict - user can continue interacting');
                    
                } else {
                    // Show error popup for other errors
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(10);
                    setIsCountdownActive(false);
                }
            }
        } else {
            // Option == 0 case (unhighlight option)
            console.log('🔄 Unhighlighting option (option = 0)');
            
            try {
                console.log('🔍 Before unhighlight updatePayload - Current zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    visitorId
                });

                // PUT zone-status/{zone_id}/payload with option = 0
                const unhighlightResult = await updatePayload({
                    payload: {
                        option: 0,
                    },
                });

                console.log('🔍 After unhighlight updatePayload - API response:', unhighlightResult);
                console.log('🔍 After unhighlight updatePayload - Updated zone status:', {
                    status: zoneStatus?.status,
                    claimedBy: zoneStatus?.claimedBy,
                    isClaimedByCurrentUser,
                    isAvailable,
                    visitorId
                });

                // Unhighlight option
                setSelectedImageId(null);
                setPreviousImage(displayedImage);
                setDisplayedImage(getImageForId(null));
                setShowDisplayedImage(!showDisplayedImage);

                // Dispatch experience live status
                dispatch(setExperienceLive(false));

                // GET zone-status/{zone_id} (as per diagram when option == 0)
                await fetchZoneStatus();
                console.log('🔄 Zone status refreshed');

            } catch (error: any) {
                console.error('❌ Failed to unhighlight option:', error);
                
                // Handle specific error types
                if (error?.status === 409) {
                    console.warn('⚠️ Zone payload unhighlight conflict (409) - maintaining user session');
                    
                    // For 409 conflicts, maintain user session regardless of API response
                    console.log('🔄 Maintaining user session during unhighlight 409 conflict');
                    
                    // Ensure user session remains active
                    if (!userHasActiveSession) {
                        setUserHasActiveSession(true);
                        console.log('✅ User session activated to maintain interaction during unhighlight conflict');
                    }
                    
                    // Still update UI optimistically
                    setSelectedImageId(null);
                    setPreviousImage(displayedImage);
                    setDisplayedImage(getImageForId(null));
                    setShowDisplayedImage(!showDisplayedImage);
                    dispatch(setExperienceLive(false));
                    
                    // Try to refresh zone status anyway
                    try {
                        await fetchZoneStatus();
                        console.log('🔄 Zone status refreshed after unhighlight conflict');
                    } catch (refreshError) {
                        console.warn('⚠️ Zone status refresh also failed:', refreshError);
                    }
                    
                    console.log('✅ UI state maintained during unhighlight 409 conflict');
                }
            }
        }
    };

    // Start button cooldown to prevent button bashing
    const startButtonCooldown = () => {
        // Clear any existing cooldown timer
        if (cooldownTimer) {
            clearTimeout(cooldownTimer);
        }

        // Set cooldown active
        setButtonCooldown(true);
        console.log('⏸️ Button cooldown started - 5 seconds until next click allowed');

        // Set timer to end cooldown
        const timer = setTimeout(() => {
            setButtonCooldown(false);
            setCooldownTimer(null);
            console.log('✅ Button cooldown ended - clicks allowed again');
        }, BUTTON_COOLDOWN_DURATION);

        setCooldownTimer(timer);
    };

    const handleCloseZoneBusyPopup = () => {
        setShowZoneBusyPopup(false);
        // Don't reset countdown - let it continue running in background
        console.log('🔕 Zone busy popup closed manually - countdown continues in background');
    };

    const handleCloseZoneTimeoutPopup = async () => {
        setShowZoneTimeoutPopup(false);
        // Reset user session when timeout popup is closed
        setUserHasActiveSession(false);
        // Reset selected button state to return all buttons to normal state
        setSelectedImageId(null);
        // Reset displayed image to default
        setPreviousImage(displayedImage);
        setDisplayedImage(imageOrangLaut00);
        setShowDisplayedImage(false);
        
        // Stop zone status polling since session ended
        stopStatusPolling();
        
        // Reset QR scan claim flag to allow new QR scans
        qrScanClaimInitiated.current = false;
        setQrScanProcessed(false);
        
        console.log('⏰ Zone timeout - resetting user session, button states, polling, and QR scan flags');
        
        // Reset zone timeout timer for next interaction
        if (zoneTimeoutTimerRef.current) {
            clearTimeout(zoneTimeoutTimerRef.current);
            zoneTimeoutTimerRef.current = null;
        }

        // Reset experience live status
        dispatch(setExperienceLive(false));
        
        // IMPORTANT: Explicitly release the zone by updating status to "available"
        try {
            console.log('🔓 Releasing zone - updating status to available...');
            await updateStatus({
                status: 'available',
                claimedBy: undefined,
                payload: { option: 0 }
            });
            console.log('✅ Zone released successfully - status updated to available');
            
            // NOTE: Do NOT refresh zone status after timeout - polling has been stopped
            // and user session should be completely ended. Refreshing would trigger
            // useEffect hooks that might restart the session.
            console.log('⚠️ Skipping zone status refresh - user session ended, polling stopped');
            
        } catch (error) {
            console.error('❌ Failed to release zone:', error);
            // NOTE: Do NOT refresh zone status after error either - same reason as above
            console.log('⚠️ Zone release failed but skipping status refresh - user session ended');
        }
    };

    // Start zone timeout when zone is claimed (processing)
    const startZoneTimeout = useCallback(() => {
        // Clear any existing timer
        if (zoneTimeoutTimerRef.current) {
            clearTimeout(zoneTimeoutTimerRef.current);
        }
        
        // Start new timeout
        const timer = setTimeout(() => {
            setShowZoneTimeoutPopup(true);
            setUserHasActiveSession(false);
            zoneTimeoutTimerRef.current = null;
            
            // IMPORTANT: Stop zone status polling immediately when timeout occurs
            // Use ref to get the latest stopStatusPolling function
            stopStatusPollingRef.current();
            
            // Reset QR scan claim flag to allow new QR scans after timeout
            qrScanClaimInitiated.current = false;
            setQrScanProcessed(false);
            qrScanProcessedRef.current = null;
            
            console.log('⏰ Zone timeout occurred - resetting user session, stopping polling, and resetting QR scan flags');
        }, ZONE_TIMEOUT_DURATION);
        
        zoneTimeoutTimerRef.current = timer;
        console.log('🕐 Zone timeout started - user has 5 minutes to interact');
    }, []);

    // Reset zone timeout on user interaction
    const resetZoneTimeout = () => {
        if (zoneTimeoutTimerRef.current) {
            clearTimeout(zoneTimeoutTimerRef.current);
            zoneTimeoutTimerRef.current = null;
            console.log('🔄 Zone timeout reset due to user interaction');
        }
    };

    // Start zone status polling (every 2 seconds as per diagram)
    const startStatusPolling = useCallback(() => {
        // IMPORTANT: Clear any existing polling timer first
        if (statusPollingTimerRef.current) {
            console.log('🛑 Clearing existing polling timer before starting new one, ID:', statusPollingTimerRef.current);
            clearInterval(statusPollingTimerRef.current);
            statusPollingTimerRef.current = null;
        }
        
        setIsPollingActive(true);
        console.log(`🔄 Starting zone status polling every ${getZonePollingIntervalSeconds()} seconds...`);
        
        // Start polling interval
        const timer = setInterval(async () => {
            try {
                console.log('📡 Polling zone status...');
                await fetchZoneStatus();
            } catch (error) {
                console.error('❌ Failed to poll zone status:', error);
                // Don't stop polling on error - continue trying
            }
        }, ZONE_STATUS_POLLING_INTERVAL_MS);
        
        statusPollingTimerRef.current = timer;
        console.log('✅ Zone status polling started with timer ID:', timer);
    }, [fetchZoneStatus]);

    // Stop zone status polling
    const stopStatusPolling = useCallback(() => {
        if (statusPollingTimerRef.current) {
            console.log('⏹️ Zone status polling stopped, clearing timer ID:', statusPollingTimerRef.current);
            clearInterval(statusPollingTimerRef.current);
            statusPollingTimerRef.current = null;
            setIsPollingActive(false);
        } else {
            console.log('⏹️ No polling timer to stop');
        }
    }, []);
    
    // Use a ref for the latest stopStatusPolling function to avoid stale closures
    const stopStatusPollingRef = useRef(stopStatusPolling);
    stopStatusPollingRef.current = stopStatusPolling;

    // Reset status polling (stop and start again)
    const resetStatusPolling = useCallback(() => {
        stopStatusPolling();
        // Small delay before restarting to avoid conflicts
        setTimeout(() => {
            startStatusPolling();
        }, 100);
    }, [stopStatusPolling, startStatusPolling]);

    // Zone claiming function for QR scan
    const handleQrScanClaim = useCallback(async () => {
        console.log('🕐 QR scan claim initiated, checking conditions...');
        console.log('🔍 visitorId:', visitorId);
        console.log('🔍 zoneStatus:', zoneStatus);
        
        // Prevent multiple simultaneous claims
        if (qrScanClaimInitiated.current) {
            console.log('⚠️ QR scan claim already in progress, skipping duplicate');
            return;
        }
        
        // Mark claim as initiated immediately to prevent duplicates
        qrScanClaimInitiated.current = true;
        
        try {
            // Check if user has uuid (visitorId) according to diagram
            if (visitorId) {
                console.log('✅ User has visitorId, attempting to claim zone...');
                
                // POST to zone-status/{zone_id}/claim
                const claimResult = await claimZone(visitorId) as ClaimResult;
                console.log('🎯 Claim result:', claimResult);
                console.log('🎯 Claim result type:', claimResult.type);
                console.log('🎯 Claim result payload:', claimResult.payload);
                
                // Check if the action was fulfilled (successful claim)
                if (claimResult.type === 'zones/claimZoneStatus/fulfilled') {
                    console.log('✅ Zone claimed successfully - setting up user session');
                    
                    // IMPORTANT: Set user session immediately upon successful claim
                    setUserHasActiveSession(true);
                    
                    // Zone claimed successfully - show "zone is playing the intro" popup
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(20);
                    setIsCountdownActive(false); // Not a real countdown, just showing intro message
                    
                    // Auto-hide intro popup after 10 seconds (intro should auto-close)
                    setTimeout(() => {
                        console.log('🎬 Auto-hiding intro popup after 10 seconds');
                        setShowZoneBusyPopup(false);
                    }, 10000);
                    
                    // Start zone timeout
                    setTimeout(() => {
                        startZoneTimeout();
                        dispatch(setExperienceLive(true));
                    }, 100);
                    
                    // Start zone status polling (every 2s as per diagram)
                    setTimeout(() => {
                        startStatusPolling();
                    }, 300);
                    
                    // Refresh zone status to get latest state
                    setTimeout(() => {
                        fetchZoneStatus();
                    }, 200);
                    
                } else {
                    console.log('❌ Zone claim failed - zone is already in use');
                    
                    // Zone claim failed - show "zone is used" popup
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(20);
                    setIsCountdownActive(true); // Real countdown for retry
                }
                
            } else {
                console.log('⚠️ User has no visitorId - attempting anonymous zone claim');
                
                // User has no uuid - try to claim zone anyway for anonymous interaction
                const claimResult = await claimZone() as ClaimResult;
                console.log('🎯 Anonymous claim result:', claimResult);
                console.log('🎯 Anonymous claim result type:', claimResult.type);
                console.log('🎯 Anonymous claim result payload:', claimResult.payload);
            
                // Check if the action was fulfilled (successful claim)
                if (claimResult.type === 'zones/claimZoneStatus/fulfilled') {
                    console.log('✅ Zone claimed successfully by anonymous user - setting up user session');
                    
                    // IMPORTANT: Set user session immediately upon successful claim
                    setUserHasActiveSession(true);
                    
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(20);
                    setIsCountdownActive(false);
                    
                    // Auto-hide intro popup after 10 seconds (intro should auto-close)
                    setTimeout(() => {
                        console.log('🎬 Auto-hiding intro popup after 10 seconds');
                        setShowZoneBusyPopup(false);
                    }, 10000);
                    
                    // Start zone timeout and dispatch experience live
                    setTimeout(() => {
                        startZoneTimeout();
                        dispatch(setExperienceLive(true));
                        fetchZoneStatus();
                    }, 100);
                    
                    // Start zone status polling (every 2s as per diagram)
                    setTimeout(() => {
                        startStatusPolling();
                    }, 300);
                    
                } else {
                    console.log('❌ Anonymous zone claim failed - zone is already in use');
                    setShowZoneBusyPopup(true);
                    setBusyPopupCountdown(20);
                    setIsCountdownActive(true);
                }
            }
            
        } catch (error) {
            console.error('❌ Failed to claim zone:', error);
            
            // Check if it's a timeout or conflict error
            const apiError = error as any;
            if (apiError?.status === 408 || apiError?.message?.includes('timeout')) {
                console.log('⏰ Zone claim timeout - zone may be busy');
            } else if (apiError?.status === 409 || apiError?.message?.includes('Conflict')) {
                console.log('⚠️ Zone conflict detected - zone is already claimed by another user');
            }
            
            // Show zone busy popup for any error
            setShowZoneBusyPopup(true);
            setBusyPopupCountdown(20);
            setIsCountdownActive(true);
            
            // Refresh zone status to get current state
            try {
                await fetchZoneStatus();
                console.log('✅ Zone status refreshed after error');
            } catch (refreshError) {
                console.error('❌ Failed to refresh zone status after error:', refreshError);
            }
            
        } finally {
            // Reset claim flag after a delay to allow for retries
            setTimeout(() => {
                qrScanClaimInitiated.current = false;
                console.log('🔄 QR scan claim flag reset - ready for new attempts');
            }, 3000); // 3 second delay before allowing new claims (increased from 2s)
        }
    }, [visitorId, zoneStatus, claimZone, startZoneTimeout, dispatch, fetchZoneStatus]);

    // Enhanced transition complete handler
    const handleTransitionComplete = () => {
        // No longer needed - we use toggle logic instead
    };

    // Function to get the image based on selected button ID
    const getImageForId = (imageId: string | null) => {
        switch (imageId) {
            case 'btn01':
                return imageOrangLaut01;
            case 'btn02':
                return imageOrangLaut02;
            case 'btn03':
                return imageOrangLaut03;
            case 'btn04':
                return imageOrangLaut04;
            default:
                return imageOrangLaut00;
        }
    };

    // Monitor zone status changes for auto button restoration (per diagram)
    useEffect(() => {
        if (zoneStatus?.payload && isPollingActive) {
            const currentOption = zoneStatus.payload.option;
            console.log('🔍 Zone payload option changed:', {
                option: currentOption,
                selectedImageId,
                isPollingActive,
                shouldRestoreButtons: currentOption === 0 && selectedImageId !== null
            });
            
            // If option is 1-4, ensure the correct button is selected (prioritize this first)
            if (currentOption >= 1 && currentOption <= 4) {
                const expectedButtonId = `btn0${currentOption}`;
                if (selectedImageId !== expectedButtonId) {
                    console.log(`🎯 Syncing button selection to option ${currentOption}`);
                    setSelectedImageId(expectedButtonId);
                    setPreviousImage(displayedImage);
                    setDisplayedImage(getImageForId(expectedButtonId));
                    setShowDisplayedImage(!showDisplayedImage);
                    dispatch(setExperienceLive(true));
                }
            }
            
            // If option is 0 (IDLE) and we have a selected button, restore all buttons
            // BUT add a small delay to avoid race conditions with button clicks
            else if (currentOption === 0 && selectedImageId !== null) {
                console.log('🔄 Detected option = 0 with selected button - checking if video actually finished...');
                
                // Add a delay to prevent premature restoration during button click processing
                setTimeout(() => {
                    // Double-check the option is still 0 after delay
                    if (zoneStatus?.payload?.option === 0 && selectedImageId !== null) {
                        console.log('🔄 Video finished playing - restoring all buttons (option = 0)');
                        
                        // Clear selected state
                        setSelectedImageId(null);
                        
                        // Reset to default image with transition
                        setPreviousImage(displayedImage);
                        setDisplayedImage(imageOrangLaut00);
                        setShowDisplayedImage(!showDisplayedImage);
                        
                        // Dispatch experience no longer live
                        dispatch(setExperienceLive(false));
                        
                        console.log('✅ All buttons restored - ready for new interaction');
                    } else {
                        console.log('⚠️ Option changed during delay - skipping restoration');
                    }
                }, 1000); // 1 second delay to allow backend processing
            }
        }
    }, [zoneStatus?.payload?.option, selectedImageId, isPollingActive, displayedImage, dispatch]);

    useEffect(() => {
        // Cleanup timers on unmount
        return () => {
            if (zoneTimeoutTimerRef.current) {
                clearTimeout(zoneTimeoutTimerRef.current);
            }
            if (cooldownTimer) {
                clearTimeout(cooldownTimer);
            }
            if (statusPollingTimerRef.current) {
                console.log('🧹 Component unmounting - cleaning up polling timer ID:', statusPollingTimerRef.current);
                clearInterval(statusPollingTimerRef.current);
                statusPollingTimerRef.current = null;
            }
        };
    }, []); // Empty dependency array - only run on mount/unmount

    // Show popup and start countdown when zone is not available AND user doesn't have active session
    useEffect(() => {
        // Don't show busy popup if timeout popup is already showing
        if (showZoneTimeoutPopup) {
            return;
        }
        
        // Debug the conditions
        console.log('🔍 Busy popup conditions check:', {
            hasZoneStatus: !!zoneStatus,
            zoneStatus: zoneStatus?.status,
            payloadOption: zoneStatus?.payload?.option,
            isPollingActive,
            isAvailable,
            userHasActiveSession,
            isClaimedByCurrentUser,
            visitorId,
            claimedBy: zoneStatus?.claimedBy,
            showZoneBusyPopup,
            isCountdownActive,
            // Additional debug info
            shouldShowPopup: zoneStatus && zoneStatus.status && !isAvailable && !userHasActiveSession && !isClaimedByCurrentUser,
            zoneClaimedByOther: zoneStatus?.claimedBy && zoneStatus.claimedBy !== visitorId,
            canUserInteract: visitorId ? isClaimedByCurrentUser : userHasActiveSession
        });
        
        // Only show popup and start countdown if zone is busy AND user doesn't have an active session AND zone is not claimed by current user
        if (zoneStatus && zoneStatus.status && !isAvailable && !userHasActiveSession && !isClaimedByCurrentUser) {
            if (!isCountdownActive) {
                // Show both popup and start countdown
                setShowZoneBusyPopup(true);
                setBusyPopupCountdown(20); // Reset countdown
                setIsCountdownActive(true);
                console.log('🚫 Zone busy for another user - showing popup + inline message with countdown');
            }
        } else {
            // Don't immediately hide the popup if it's showing an intro message (isCountdownActive = false)
            // This allows the intro popup to stay visible after successful claim
            if (showZoneBusyPopup && !isCountdownActive) {
                console.log('🎬 Keeping intro popup visible - user just claimed zone');
                return; // Don't hide the intro popup
            }
            
            // Zone is available OR user has active session OR zone is claimed by current user - reset everything
            setShowZoneBusyPopup(false);
            setIsCountdownActive(false);
            
            // IMPORTANT: Don't automatically reset user session when zone becomes available
            // The user session should persist even after the zone intro period ends
            // Only reset if the user explicitly times out or manually resets
            if (isAvailable && !userHasActiveSession) {
                // Only reset QR scan flags if user doesn't have an active session
                qrScanClaimInitiated.current = false;
                setQrScanProcessed(false);
                qrScanProcessedRef.current = null;
                console.log('✅ Zone available and no active session - resetting QR scan flags only');
            } else if (userHasActiveSession) {
                console.log('👤 User has active session - maintaining session despite zone availability');
            } else if (isClaimedByCurrentUser) {
                console.log('🔑 Zone claimed by current user - no popup needed');
            }
        }
    }, [zoneStatus, isAvailable, isCountdownActive, userHasActiveSession, showZoneTimeoutPopup, isClaimedByCurrentUser, visitorId, showZoneBusyPopup]);

    // Detect zone state transitions and reset user session appropriately
    useEffect(() => {
        console.log('🔍 Zone state transition check:', {
            isAvailable,
            userHasActiveSession,
            visitorId,
            isClaimedByCurrentUser,
            zoneStatus: zoneStatus?.status,
            claimedBy: zoneStatus?.claimedBy
        });
        
        // IMPORTANT: Only reset user session in very specific circumstances
        // Do NOT reset during API conflicts or temporary inconsistencies
        
        // For registered users: only reset if zone is definitively claimed by someone else
        if (visitorId && userHasActiveSession && zoneStatus) {
            const isDefinitelyClaimedByOther = zoneStatus.claimedBy && 
                                             zoneStatus.claimedBy !== visitorId && 
                                             zoneStatus.status === 'processing';
            
            if (isDefinitelyClaimedByOther) {
                console.log('🔄 Zone definitively claimed by another user - resetting session');
                setUserHasActiveSession(false);
                stopStatusPolling(); // Stop polling when session ends
                qrScanClaimInitiated.current = false;
                setQrScanProcessed(false);
                qrScanProcessedRef.current = null;
                setSelectedImageId(null);
                setPreviousImage(displayedImage);
                setDisplayedImage(imageOrangLaut00);
                setShowDisplayedImage(false);
            } else {
                console.log('⚠️ Keeping user session active - zone state may be temporary');
            }
        }
        
        // For non-registered users: only reset if zone is definitively claimed by someone else
        if (!visitorId && userHasActiveSession && zoneStatus) {
            const isDefinitelyClaimedByOther = zoneStatus.claimedBy && zoneStatus.status === 'processing';
            
            if (isDefinitelyClaimedByOther) {
                console.log('🔄 Zone definitively claimed by another user (non-registered) - resetting session');
                setUserHasActiveSession(false);
                stopStatusPolling(); // Stop polling when session ends
                qrScanClaimInitiated.current = false;
                setQrScanProcessed(false);
                qrScanProcessedRef.current = null;
                setSelectedImageId(null);
                setPreviousImage(displayedImage);
                setDisplayedImage(imageOrangLaut00);
                setShowDisplayedImage(false);
            } else {
                console.log('⚠️ Keeping non-registered user session active - zone state may be temporary');
            }
        }
        
        // NOTE: Removed automatic session reset when zone becomes "available"
        // This was causing issues during 409 conflicts where the zone temporarily shows as available
        // Sessions should only be reset through explicit user actions (timeout, manual reset, etc.)
        
    }, [visitorId, userHasActiveSession, zoneStatus, displayedImage]);

    // Countdown timer for busy popup (continues even when popup is closed)
    useEffect(() => {
        if (!isCountdownActive || busyPopupCountdown <= 0) {
            if (busyPopupCountdown <= 0 && isCountdownActive) {
                // Countdown finished - reset state and refresh zone status
                setIsCountdownActive(false);
                setShowZoneBusyPopup(false);
                console.log('⏰ Countdown finished - refreshing zone status...');
                // Trigger zone status refresh
                fetchZoneStatus();
            }
            return;
        }

        const timer = setTimeout(() => {
            setBusyPopupCountdown(prev => {
                const newCount = prev - 1;
                console.log(`⏳ Zone busy countdown: ${newCount} seconds remaining`);
                return newCount;
            });
        }, 1000);

        return () => clearTimeout(timer);
    }, [isCountdownActive, busyPopupCountdown, fetchZoneStatus]);

    // Handle QR scan automatic zone claiming according to diagram flow
    useEffect(() => {
        const fromQrScan = searchParams.get('fromQrScan');
        const qrTimestamp = searchParams.get('qrTimestamp');
        console.log('🔍 QR scan useEffect triggered:', { 
            fromQrScan, 
            qrTimestamp, 
            visitorId, 
            zoneStatus, 
            processed: qrScanProcessed, 
            claimInitiated: qrScanClaimInitiated.current,
            processedTimestamp: qrScanProcessedRef.current
        });
        
        if (fromQrScan === 'true') {
            // Improved logic: Check if this is a new QR scan by comparing timestamp
            // If no timestamp is provided, treat it as a new scan if no claim is currently in progress
            let isNewQrScan = false;
            
            if (qrTimestamp) {
                // If we have a timestamp, check if it's different from the last processed one
                isNewQrScan = qrTimestamp !== qrScanProcessedRef.current;
                console.log('🔍 Timestamp-based scan check:', {
                    currentTimestamp: qrTimestamp,
                    lastProcessed: qrScanProcessedRef.current,
                    isNewScan: isNewQrScan
                });
            } else {
                // If no timestamp, check if no claim is in progress
                isNewQrScan = !qrScanClaimInitiated.current;
                console.log('🔍 No timestamp - checking claim status:', {
                    claimInProgress: qrScanClaimInitiated.current,
                    isNewScan: isNewQrScan
                });
            }
            
            if (isNewQrScan) {
                console.log('🎯 New QR scan detected - following diagram flow...');
                console.log('🔍 Current user state:', { visitorId, hasVisitorId: !!visitorId });
                console.log('🔍 Current zone status:', zoneStatus);
                console.log('🔍 QR timestamp:', qrTimestamp);
                
                // Mark this timestamp as processed to prevent duplicate processing
                setQrScanProcessed(true);
                if (qrTimestamp) {
                    qrScanProcessedRef.current = qrTimestamp;
                }
                
                // Clear the parameters from URL to avoid repeated claims
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('fromQrScan');
                newSearchParams.delete('qrTimestamp');
                setSearchParams(newSearchParams, { replace: true });
                
                console.log('🔍 About to call handleQrScanClaim for new scan');
                // Call the claiming function directly (only once per QR scan)
                // NOTE: Don't set qrScanClaimInitiated here - let handleQrScanClaim manage it
                handleQrScanClaim();
            } else {
                console.log('⚠️ QR scan already processed, skipping duplicate call');
            }
        }
    }, [searchParams, visitorId, setSearchParams, handleQrScanClaim]);

    // Dynamic card content based on zone status
    const getCardContent = () => {
        // Priority 1: If a button is selected (video is playing), show video playing message
        if (selectedImageId) {
            console.log('🎬 Video playing - showing video playing message');
            return t('exhibitShared.videoPlaying');
        }
        
        // Priority 2: If user has an active session, they can always interact
        if (userHasActiveSession) {
            console.log('🎯 User has active session - showing interact message');
            return t('exhibitShared.selectToInteract');
        }
        
        // Priority 3: Check if user can interact based on zone ownership
        const userCanInteract = canUserInteract();
        
        // For registered users
        if (visitorId) {
            // If user can interact (zone claimed by them), show interact message
            if (isClaimedByCurrentUser) {
                console.log('🔑 Zone claimed by current user - showing interact message');
                return t('exhibitShared.selectToInteract');
            }
            
            // If zone is available and user doesn't have active session, show available to claim
            if (isAvailable && !userHasActiveSession) {
                console.log('🆓 Zone available for claiming');
                return t('exhibitShared.availableToClaim');
            }

            // Default: prompt to scan QR
            console.log('📱 Prompting to scan QR code');
            return t('exhibitShared.scanQrToClaim');
            
        } else {
            // For non-registered users
            // If zone is available, show available to claim
            if (isAvailable && !userHasActiveSession) {
                console.log('🆓 Zone available for non-registered user');
                return t('exhibitShared.availableToClaim');
            }
            
            // If zone is claimed by someone else, prompt to scan QR
            if (zoneStatus?.claimedBy) {
                console.log('📱 Zone claimed by someone else - prompting QR scan');
                return t('exhibitShared.scanQrToClaim');
            }
        }
        
        // If zone is busy and user doesn't have active session, show waiting message
        if (zoneStatus && zoneStatus.status && !isAvailable && !userHasActiveSession) {
            if (isCountdownActive) {
                console.log('⏳ Zone busy - showing countdown');
                return t('exhibitShared.zoneInAction', { seconds: busyPopupCountdown });
            }
        }
        
        // Default message - prioritize interaction if user has session
        console.log('🔄 Default message - user session:', userHasActiveSession);
        return userHasActiveSession ? t('exhibitShared.selectToInteract') : t('exhibitShared.scanQrToClaim');
    };

    // Show loading state while images are being cached
    if (isLoading || !isComplete) {
        return <LoadingScreen message="Loading images..." />;
    }

    // Main exhibit screen
    return (
        <div className={`
            main-content-wrapper bg-black 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            <TcPaper classNameStyle="my-2">
                <h1>{t('exhibits.orangLaut.title')}</h1>
            </TcPaper>
            
            <TcCrossFadeImage
                imageA={showDisplayedImage ? displayedImage : previousImage}
                imageB={showDisplayedImage ? previousImage : displayedImage}
                showImageA={showDisplayedImage}
                alt={t('exhibits.orangLaut.title')}
                classNameStyle="mb-6"
                duration={800}
                onTransitionComplete={handleTransitionComplete}
                preloadImages={false} // Disable internal preloading since we handle it at page level
            />

            <TcCard
                cardContent={getCardContent()}
            >
                {/* Interactive Image Buttons */}
                <div className="mb-0">
                    <div className="grid grid-cols-4 gap-1 mb-6">
                        {imageButtons.map((button, index) => (
                            <div
                                key={button.id}
                                onClick={() => {
                                    // Use the enhanced interaction check that considers both zone status and user session
                                    const canInteract = canUserInteract();
                                    
                                    if (canInteract && !buttonCooldown) {
                                        // Map button IDs to option numbers: btn01=1, btn02=2, btn03=3, btn04=4
                                        const optionNumber = parseInt(button.id.replace('btn', '').replace(/^0+/, '')) || 0;
                                        
                                        // If clicking the same button again, unhighlight it (option = 0)
                                        if (selectedImageId === button.id) {
                                            handleButtonClick(button.id, button.title, 0); // Unhighlight
                                        } else {
                                            handleButtonClick(button.id, button.title, optionNumber); // Highlight new option
                                        }
                                    }
                                }}
                                className={`
                                    exhibit-image-button 
                                    ${(() => {
                                        // Use the enhanced interaction check
                                        const canInteract = canUserInteract();
                                        
                                        if (!canInteract) {
                                            return 'opacity-30 cursor-not-allowed pointer-events-none';
                                        }
                                        
                                        // Check if buttons are in cooldown
                                        if (buttonCooldown) {
                                            return 'cursor-wait pointer-events-none';
                                        }
                                        
                                        // Buttons are interactive
                                        return 'cursor-pointer';
                                    })()}
                                    ${(() => {
                                        // Handle button highlighting and dimming
                                        if (selectedImageId) {
                                            // A button is selected
                                            if (selectedImageId === button.id) {
                                                // This is the selected button - highlight it
                                                return 'border-white opacity-100';
                                            } else {
                                                // Other buttons - dim them
                                                return 'opacity-50 border-transparent';
                                            }
                                        } else {
                                            // No button selected - all buttons normal
                                            return 'border-transparent opacity-100';
                                        }
                                    })()}
                                `}
                            >
                                <img 
                                    src={button.image} 
                                    alt={button.title}
                                    className="w-full h-auto object-cover"
                                />
                            </div>
                        ))}
                    </div>
                    
                    {/* Selected Content */}
                    {selectedImageId && (
                        <div className="selected-button-content">
                            {(() => {
                                const selectedButton = imageButtons.find(btn => btn.id === selectedImageId);
                                return selectedButton ? (
                                    <>
                                        <h3 dangerouslySetInnerHTML={{ __html: selectedButton.title }} />
                                        <p dangerouslySetInnerHTML={{ __html: selectedButton.content }} />
                                    </>
                                ) : null;
                            })()}
                        </div>
                    )}
                </div>
            </TcCard>
            
            {!selectedImageId && <TcPaper classNameStyle="general-content">
                <h2 dangerouslySetInnerHTML={{ __html: t('exhibits.orangLaut.subtitle') }} />
                <p dangerouslySetInnerHTML={{ __html: t('exhibits.orangLaut.description') }} />
                

            </TcPaper>}
            

            <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0" />

            {/* Zone Busy/Intro Popup */}
            {showZoneBusyPopup && (
                <TcPopupR65 
                    handleClose={handleCloseZoneBusyPopup}
                    classNameStyle={`${showZoneBusyPopup ? 'opacity-100' : 'opacity-0'} has-transition`}
                >
                    <div className="text-center">
                        {/* Show different messages based on countdown state */}
                        {isCountdownActive ? (
                            // Zone is used - show countdown
                            <>
                                <h2 className="font-semibold mb-4">{t('exhibitShared.zoneInUseTitle')}</h2>
                                <p>{t('exhibitShared.zoneInUseMessage')}</p>
                                <p className="font-semibold">
                                    {t('exhibitShared.retryIn', { seconds: busyPopupCountdown })}
                                </p>
                            </>
                        ) : (
                            // Zone is playing intro - no countdown
                            <>
                                <h2 className="font-semibold mb-4">{t('exhibitShared.zoneIntroTitle')}</h2>
                                <p>{t('exhibitShared.zoneIntroMessage')}</p>
                            </>
                        )}
                    </div>
                </TcPopupR65>
            )}

            {/* Zone timeout popup */}
            {showZoneTimeoutPopup && (
                <TcPopupR65 
                    hasCloseButton={true}
                    handleClose={handleCloseZoneTimeoutPopup}
                >
                    <div className="text-center">
                        <h2 className="font-semibold mb-4" dangerouslySetInnerHTML={{ __html: t('popup.timeoutTitle') }} />
                        <p dangerouslySetInnerHTML={{ __html: t('popup.timeoutContent') }} />
                    </div>
                </TcPopupR65>
            )}

            {/* Note: Global timeout and busy systems disabled for zone-based exhibits */}
            {/* Zone-based exhibits use their own zone-specific timeout and busy systems */}
        </div>
    );
} 